// The curated Grasshopper script library.
//
// These are FIXED, hand-authored, tested components — not AI generations. That is the
// whole point of the distinction in the sidebar: the GH Script Generator writes fresh
// code from a prompt (and re-derives it, with all the variance that implies), whereas
// everything here is a script that has been validated against RhinoCommon and pinned by
// tests under apps/design/docs/grasshopper/tests/.
//
// The Python is pulled straight out of the golden with a Vite ?raw import, so the file
// the tests run against and the file the team downloads are the same bytes — there is
// no second copy to drift.
//
// Adding a script:
//   1. drop the golden in docs/grasshopper/examples/
//   2. add an entry here
//   3. add one <Route> in App.jsx
// The sidebar picks it up automatically.
import perforationSrc from '../docs/grasshopper/examples/perforation-generator.py?raw'

// Orient 3-Point was REMOVED on 2026-08-01 at Deepak's instruction. The shipped
// .gh needs rebuilding against Rhino 8 (it depends on the office LAN Zoo), and
// the tool it exposes is superseded by ICP/surface registration — a three-point
// alignment reports a confident residual even when the fit is badly wrong, which
// is the worst failure mode for a positioning tool. Do NOT re-add it without
// that rebuild. The .py/.gh assets stay in docs/grasshopper/ for the rebuild.
export const GH_SCRIPTS = [
  {
    id: 'perforation',
    route: '/gh-perforation',
    label: 'Surface Perforation',
    title: 'Surface Perforation — uniform holes on any surface',
    blurb:
      'Fills a surface or Brep with a hexagonal lattice of holes, every one equidistant ' +
      'from its neighbours, measured along the surface — so the spacing holds on steep ' +
      'and doubly curved shapes such as an orbital implant, not just on the flat.',
    pyName: 'osteo3d-perforation.py',
    ghName: 'osteo3d-perforation.gh',
    ghUrl: '/gh/perforation.gh',
    ghStep: 'Set the Srf input to your surface (right-click \u2192 Set one Surface), then adjust the D and G sliders. Spacing is always D + G \u2014 leave Count at 0 unless you need a hard cap.',
    pyStep: 'Set Srf to Type hint \u2192 Brep, NOT Surface. A Surface hint hands the script the untrimmed base surface, so the trim is discarded and holes cover the whole underlying sheet instead of your shape. Leave the rest as numbers. Srf takes one face \u2014 explode a polysurface first, or feed its largest face.',
    outNote: 'this component\u2019s report (hole count, achieved spacing, and any surface warnings)',
    warning: 'Spacing is set by D + G, not by Count. Count only caps how many holes are kept \u2014 raising it will never move the holes closer together. If you need them tighter, reduce G.',
    code: perforationSrc,
    inputs: [
      ['Srf', 'Brep', 'the surface / Brep to perforate — feed the TRIMMED one'],
      ['D', 'float', 'hole diameter'],
      ['G', 'float', 'edge-to-edge gap. Centre-to-centre spacing is always D + G'],
      ['B', 'float', 'boundary offset — margin from the trimmed edge'],
      ['Seed', 'int', 'rotates the lattice, in degrees. Deterministic'],
      ['Iter', 'int', 'inert — a lattice is uniform by construction. Kept so the script drops into the older box'],
      ['Count', 'int', 'optional CAP on hole count (0 = no cap)'],
    ],
    outputs: [
      ['P', 'Point3d', 'hole centre points'],
      ['Pl', 'Plane', 'tangent plane at each point'],
      ['C', 'Circle', 'the perforation circles'],
    ],
    notes: [
      'Spacing is ALWAYS centre-to-centre = D + G. To move the holes further apart, ' +
        'raise G — that is the only control that changes spacing.',
      'Count is a CAP, never a spacing control: it thins the finished lattice evenly ' +
        'and leaves the spacing alone. Set 0 for no cap. If you previously had to raise ' +
        'Count to get the look you wanted, you were working around a bug that is fixed ' +
        'here — raise G instead.',
      'Iter is inert (a lattice is uniform by construction, so there is nothing to ' +
        'relax) and Seed only rotates the pattern. Feed the trimmed surface so B holds.',
      'If holes appear spread well beyond your shape, the Srf input is receiving the ' +
        'UNTRIMMED base surface. In Grasshopper a Surface type hint discards the trim; ' +
        'set the hint to Brep. The downloaded .gh is already hinted correctly \u2014 this ' +
        'only bites when the component is built by hand from the copied code. The out ' +
        'panel says so explicitly when it happens.',
      'The script generates the pattern twice — once carrying the continuous lattice ' +
        'to 40 degrees of slope, once to 60 — measures how much surface each leaves ' +
        'visibly uncovered, and keeps the evener one. The out panel reports the choice ' +
        'and both measurements every run. The figure counts the whole surface. When B ' +
        'is above zero the panel also says what share of the surface is rim (the strip ' +
        'within B of the trimmed edge, where B holds the holes back) and what share is ' +
        'inside the pattern. A lower B narrows the rim. On most cranial and orbital shells the ' +
        '60-degree pattern wins: more of the surface is one seamless grid. On slopes ' +
        'the gaps open smoothly — up to about 2x at 60 degrees — while the minimum ' +
        'spacing stays exactly D + G.',
      'Small gaps where the pattern grows around a curve are filled automatically. Every ' +
        'filled hole keeps the full D + G spacing; the pass only claims spots that were ' +
        'already large enough to hold a hole.',
      'Beyond the chosen slope limit — orbital walls, steep rims — the pattern is grown along ' +
        'the surface instead, and there it cannot be a flawless grid: a doubly curved ' +
        'shape cannot be tiled by a regular hexagonal lattice at all, the same reason a ' +
        'geodesic dome needs twelve pentagons. Expect some irregularity only in those ' +
        'steep zones. No hole is ever closer than D + G, anywhere.',
    ],
  },
]

export function findGhScript(id) {
  return GH_SCRIPTS.find((s) => s.id === id)
}
